import { NO_ERP_INTEGRATION_MESSAGE, type CostingTransactionLogDto, type PostingLogStatusFilter } from '@/services/postingLogApi';
import { getApiErrorStatus } from '@/services/reportPaging';
import type { PostingStatus, PostingLogRow, PostingLogFilters, PostingLogQuery, SortKey, SortDir } from '@/utils/PostingLog/types';

// ─── API mapping ──────────────────────────────────────────────────────────────

export const mapPostingLogRow = (dto: CostingTransactionLogDto): PostingLogRow => ({
  id: String(dto.id),
  logId: dto.id,
  timesheetId: String(dto.timesheetId),
  employeeId: dto.employeeId,
  employeeName: dto.employeeName ?? `Employee ${dto.employeeId}`,
  transactionDate: dto.transactionDate ? new Date(dto.transactionDate) : undefined,
  projectCode: dto.projectCode ?? '',
  projectName: dto.projectName ?? dto.projectCode ?? '—',
  costCentreCode: dto.costCentreCode ?? '',
  costCentreName: dto.costCentreName ?? dto.costCentreCode ?? '—',
  quantity: dto.quantity ?? 0,
  price: dto.price ?? 0,
  upliftValue: dto.upliftValue ?? 0,
  status: dto.success ? 'Posted' : 'Failed',
  externalTransactionId: dto.externalTransactionId ?? undefined,
  errorMessage: dto.errorMessage ?? undefined,
});

/**
 * Resolves the status filters to the single `status` the API accepts.
 * `undefined` means no status filter; `null` means no posting line can match
 * (the log only records posted or failed lines).
 */
export const resolveStatusFilter = (filters: PostingLogFilters): PostingLogStatusFilter | undefined | null => {
  let allowed: PostingStatus[] = filters.statuses?.length ? filters.statuses : ['Posted', 'Failed'];
  if (filters.failedOnly) allowed = allowed.filter((s) => s === 'Failed');
  const posted = allowed.includes('Posted');
  const failed = allowed.includes('Failed');
  if (posted && failed) return undefined;
  if (posted) return 'posted';
  if (failed) return 'failed';
  return null;
};

export const describeRetryError = (error: unknown): string => {
  switch (getApiErrorStatus(error)) {
    case 503:
      return NO_ERP_INTEGRATION_MESSAGE;
    case 403:
      return "You don't have permission to retry postings.";
    case 404:
      return 'This posting line could not be found.';
    default: {
      const errors = (error as { data?: { errors?: unknown } })?.data?.errors;
      if (Array.isArray(errors) && typeof errors[0] === 'string') return errors[0];
      return 'The ERP returned an error. Please try again.';
    }
  }
};

export const toPostingLogQuery = (
  filters: PostingLogFilters,
  search: string,
  sortKey: SortKey,
  sortDir: SortDir,
): PostingLogQuery => ({
  employeeIds: filters.employeeIds,
  projectCodes: filters.projectIds,
  costCentreCodes: filters.costCentreIds,
  departments: filters.departments,
  status: resolveStatusFilter(filters) ?? undefined,
  dateFrom: filters.dateFrom,
  dateTo: filters.dateTo,
  search: search || undefined,
  sortBy: sortKey,
  sortDir,
});
